import React, { useState } from 'react';
import ColumnDatePicker from 'components/ColumnDatePicker';
import moment from 'moment';
import { calendarDaysApart } from 'lib/helpers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { ColumnButton } from 'lib/components/ColumnButton';
import { ColumnSelect } from 'lib/components/ColumnSelect';
import { useReportParams } from '../context/ReportParamsContext';
import { SELECT_DATE_RANGES } from '../constants/dateRanges';

type DateRangePickerWithModalProps = {
  value: string;
};

export function DateRangePickerWithModal({
  value
}: DateRangePickerWithModalProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);

  const { getDateRange, updateDateRange, handleDateRangeChange } =
    useReportParams();

  const { startDate: startDateStr, endDate: endDateStr } = getDateRange();

  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(
    startDateStr ? new Date(startDateStr) : undefined
  );
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>(
    endDateStr ? new Date(endDateStr) : undefined
  );

  const handleChange = (newValue: string) => {
    if (newValue === 'custom') {
      setIsDropdownOpen(true);
      setSelectedValue(newValue);
      return;
    }
    setSelectedValue(newValue);
    updateDateRange(newValue);
  };

  const handleApply = () => {
    if (customStartDate && customEndDate) {
      handleDateRangeChange(customStartDate, customEndDate);
      setIsDropdownOpen(false);
    }
  };

  const handleClose = () => {
    setIsDropdownOpen(false);
    const { dateRange } = getDateRange();
    if (dateRange !== 'custom') {
      setCustomStartDate(undefined);
      setCustomEndDate(undefined);
    }
  };

  return (
    <div className="flex flex-col gap-2 relative">
      <ColumnSelect
        options={Object.values(SELECT_DATE_RANGES)}
        onChange={handleChange}
        value={selectedValue}
        allowUndefined
        id="date-range-select"
        labelText="Select Date Range"
      />
      {isDropdownOpen && (
        <div className="absolute mt-3 right-0 w-[2000px] bg-white rounded-md shadow-lg z-10">
          <div className="p-4">
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-2 w-[1800px]">
                <ColumnDatePicker
                  className="w-full text-l h-16"
                  format="MMM dd"
                  momentFormat="MMM DD"
                  placeholderText="Start date"
                  value={customStartDate}
                  onChange={setCustomStartDate}
                  shouldDisableDate={(date: MaterialUiPickersDate) => {
                    if (!date) {
                      return false;
                    }
                    return !!(
                      customEndDate &&
                      calendarDaysApart(
                        moment(date).startOf('day').toDate(),
                        moment(customEndDate).startOf('day').toDate()
                      ) > 0
                    );
                  }}
                />
                <ColumnDatePicker
                  className="w-full text-l h-16"
                  format="MMM dd"
                  momentFormat="MMM DD"
                  placeholderText="End date"
                  value={customEndDate}
                  onChange={setCustomEndDate}
                  shouldDisableDate={(date: MaterialUiPickersDate) => {
                    if (!date) {
                      return false;
                    }
                    return !!(
                      customStartDate &&
                      calendarDaysApart(
                        moment(customStartDate).startOf('day').toDate(),
                        moment(date).startOf('day').toDate()
                      ) > 0
                    );
                  }}
                />
              </div>

              <div className="flex justify-end gap-4">
                <ColumnButton
                  buttonText="Cancel"
                  secondary
                  onClick={handleClose}
                  type={'button'}
                />
                <ColumnButton
                  buttonText="Apply"
                  primary
                  onClick={handleApply}
                  type={'button'}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
